import { defaultHover, MultilineInput } from '@amelio/ui-library';
import { useEffect, useState } from 'react';

import {
    surveyEmoji1,
    surveyEmoji2,
    surveyEmoji3,
    surveyEmoji4,
    surveyEmoji5,
} from '@/utils/emojis';

export interface EmojiOption {
    /** Unique identifier for the emoji (optional) */
    id?: string;
    /** Image source URL for the emoji */
    src: string;
    /** Label text displayed below the emoji */
    label: string;
    /** Accessible alt text for the emoji image */
    alt?: string;
}

export interface QuestionEmojiListProps {
    // Array of emoji options. Defaults to 5 standard emojis.
    emojis?: EmojiOption[];

    /**
     * Fires when a label text is updated.
     * Returns: { index, value }
     */
    onLabelUpdate?: (args: { index: number; value: string }) => void;

    // Callback fired when an emoji is selected.
    onEmojiSelect?: (index: number) => void;

    /**
     * Optional external value for the selected emoji.
     * Supports controlled/uncontrolled usage.
     */
    selectedIndex?: number;

    // Custom CSS classes.
    className?: string;
}

// Default emoji icons used by the builder UI (ordered best → worst → top to bottom display).
// Persistence in DB still follows the canonical worst → best convention (1 = worst, 5 = best); the inversion happens in EmojiChoiceQuestion.
export const DEFAULT_EMOJIS: EmojiOption[] = [
    { src: surveyEmoji1, label: 'Very Satisfied' },
    { src: surveyEmoji2, label: 'Satisfied' },
    { src: surveyEmoji3, label: 'Neutral' },
    { src: surveyEmoji4, label: 'Unsatisfied' },
    { src: surveyEmoji5, label: 'Very Unsatisfied' },
];

export const QuestionEmojiList = ({
    emojis = DEFAULT_EMOJIS,
    onLabelUpdate,
    onEmojiSelect,
    selectedIndex,
    className = '',
}: QuestionEmojiListProps) => {
    const [internalEmojis, setInternalEmojis] = useState<EmojiOption[]>(emojis);
    const [internalSelected, setInternalSelected] = useState<number | null>(null);

    useEffect(() => {
        setInternalEmojis(emojis);
    }, [emojis]);

    const isControlled = selectedIndex !== undefined;
    const activeIndex = isControlled ? selectedIndex : internalSelected;

    type LabelChange = {
        index: number;
        value: string;
    };

    /**
     * Handles label updates using a typed object parameter.
     */
    const handleLabelChange = ({ index, value }: LabelChange) => {
        setInternalEmojis((prev) =>
            prev.map((item, i) => (i === index ? { ...item, label: value } : item)),
        );

        onLabelUpdate?.({ index, value });
    };

    /**
     * Handles emoji selection.
     */
    const handleEmojiClick = (index: number) => {
        if (!isControlled) {
            setInternalSelected(index);
        }

        onEmojiSelect?.(index);
    };

    return (
        <div className={`flex flex-col gap-3 ${className}`}>
            {internalEmojis.map((item, index) => {
                const isSelected = activeIndex === index;

                return (
                    <div
                        data-testid={'emoji-row'}
                        key={index}
                        className={'flex items-center gap-3 rounded-xl px-2 py-2'}
                        onClick={() => handleEmojiClick(index)}
                    >
                        {/* Icon (React component) */}
                        <div className={'flex h-9 w-9 items-center justify-center'}>
                            <img
                                src={item.src}
                                alt={item.label}
                            />
                        </div>

                        {/* Editable label */}
                        <div
                            // The wrapper's own border is the selection mark; when the inner field takes
                            // focus it hands the outline over rather than drawing a second one.
                            className={`flex-1 rounded-lg border border-transparent ${defaultHover} hover:border-base-300 ${
                                isSelected
                                    ? 'bg-base-200 focus-within:border-primary border-gray-300'
                                    : 'bg-surface-field focus-within:border-transparent'
                            }`}
                        >
                            <MultilineInput
                                size={'md'}
                                value={item.label}
                                onChange={(value) => handleLabelChange({ index, value })}
                                placeholder={'Label'}
                                rows={1}
                                autoGrow={true}
                            />
                        </div>
                    </div>
                );
            })}
        </div>
    );
};
